import type { CartItem, ProductLot } from '@/types'

/**
 * Sale-side on-hand cap for a cart line. Mirrors the modal's
 * `getMaxForKey` so both surfaces agree on what "no more on hand" means.
 *
 * Precedence:
 *   1. `pos.products.verify_if_is_over_on_hand` is false → no cap (Infinity).
 *   2. Locked lot with a known batch quantity → that batch's stock.
 *   3. SKU on-hand → `product.on_hand ?? product.quantity_available`.
 *   4. Stock unknown → no cap (null), so we don't accidentally block sales
 *      on products without inventory tracking.
 *
 * Refund items return null — refund caps come from `max_refund_qty` and
 * are enforced separately. Callers should skip the cap for refund lines.
 */
export function computeSaleCap(
  item: CartItem | null,
  batches: ProductLot[] | undefined,
  verifyOverOnHand: boolean,
): number | null {
  if (!item) return null
  if (item.quantity < 0) return null
  if (!verifyOverOnHand) return Infinity

  if (item.lot_number && batches) {
    const batch = batches.find((b) => b.lot_number === item.lot_number) ?? null
    if (batch?.quantity != null && Number.isFinite(Number(batch.quantity))) {
      return Math.max(0, Math.floor(Number(batch.quantity)))
    }
  }

  const onHandRaw = item.product.on_hand ?? item.product.quantity_available
  const onHandNum = typeof onHandRaw === 'number'
    ? onHandRaw
    : parseFloat(String(onHandRaw ?? ''))
  return Number.isFinite(onHandNum) ? Math.max(0, Math.floor(onHandNum)) : null
}

/** True when the line is at or over its sale-side cap. */
export function isAtSaleCap(quantity: number, cap: number | null): boolean {
  if (cap === null) return false
  if (!Number.isFinite(cap)) return false
  return quantity >= cap
}
